// Telemetria di PRODOTTO lato app (Lean Startup — innovation accounting).
//
// Distinta dal debug logger (debugLog.ts): qui tracciamo *come* l'utente usa
// l'app (apertura feature) per i segnali NON derivabili dagli `events` di
// compliance. Le metriche actionable principali (activation/retention/TTFL)
// arrivano comunque dagli `events` server-side: questo canale è secondario.
//
// PRIVACY: attiva di default (base giuridica: esecuzione del contratto +
// legittimo interesse per un servizio B2B a pagamento; dati pseudonimi per
// tenant, niente PII, niente profilazione individuale — vedi privacy policy
// §10). Disattivabile in qualsiasi momento da Impostazioni → Diagnostica:
// l'opt-out scrive il flag '0' e svuota la coda non ancora inviata.
//
// Mai PII nei props: solo identificatori tecnici. Mai throw: la telemetria
// non deve rompere l'app.

import { getSupabase, SUPABASE_URL } from '../supabase.js';

const CONSENT_KEY = 'trackeat.analytics_consent_v1';
const QUEUE_KEY = 'trackeat.analytics_queue_v1';
const QUEUE_MAX = 300;
const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_BATCH = 50;

interface QueuedEvent {
  name: string;
  ts: string;
  props?: Record<string, unknown>;
}

// Attiva di default: raccoglie a meno che l'utente non abbia esplicitamente
// disattivato (flag '0'). Se localStorage non è accessibile, di default ON.
export function analyticsConsentGiven(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setAnalyticsConsent(on: boolean): void {
  try {
    if (on) localStorage.setItem(CONSENT_KEY, '1');
    else {
      localStorage.setItem(CONSENT_KEY, '0'); // opt-out esplicito e persistente
      localStorage.removeItem(QUEUE_KEY); // svuota la coda non ancora spedita
    }
  } catch {
    /* noop */
  }
}

function readQueue(): QueuedEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedEvent[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(q: QueuedEvent[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-QUEUE_MAX)));
  } catch {
    /* noop */
  }
}

/** Registra un evento di prodotto. No-op senza consenso. Mai throw. */
export function track(name: string, props?: Record<string, unknown>): void {
  if (!analyticsConsentGiven()) return;
  if (!/^[a-z0-9_]{1,80}$/.test(name)) return;
  try {
    const q = readQueue();
    q.push({ name, ts: new Date().toISOString(), props });
    writeQueue(q);
  } catch {
    /* noop */
  }
}

let flusherTimer: ReturnType<typeof setInterval> | null = null;
let flushing = false;

async function flushToServer(): Promise<void> {
  if (flushing || !analyticsConsentGiven() || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
  flushing = true;
  try {
    const q = readQueue();
    if (q.length === 0) return;
    const supabase = getSupabase();
    const sess = await supabase.auth.getSession();
    if (supabase !== getSupabase() || (typeof navigator !== 'undefined' && !navigator.onLine)) return;
    const token = sess.data.session?.access_token;
    if (!token) return; // pre-auth: resta in coda locale

    const batch = q.slice(0, FLUSH_BATCH);
    const res = await fetch(`${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/product-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ source: 'mobile', events: batch }),
    });
    if (res.ok) {
      writeQueue(q.slice(batch.length)); // rimuovi solo ciò che è andato a buon fine
    }
  } catch {
    // best-effort: offline-first, si riprova al prossimo tick
  } finally {
    flushing = false;
  }
}

export function startAnalyticsFlusher(): void {
  if (flusherTimer) return;
  flusherTimer = setInterval(() => {
    void flushToServer();
  }, FLUSH_INTERVAL_MS);
}
